import React, { useEffect, useState, useCallback, useRef } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import SearchBar from '../../components/common/SearchBar';
import Pagination from '../../components/common/Pagination';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StatusBadge from '../../components/common/StatusBadge';
import ToastStack, { useToasts } from '../../components/common/Toast';
import { useAuth } from '../../context/AuthContext';
import {
  getAdminProductReviews, updateProductReviewVisibility, deleteProductReview,
  getAdminStoreReviews, updateStoreReviewVisibility, updateStoreReviewReward, deleteStoreReview,
  reviewStreamUrl,
} from '../../api/review';
import { formatDateTime } from '../../utils/format';

const USE_YN_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'Y', label: '노출' },
  { value: 'N', label: '숨김' },
];

const REWARD_YN_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'Y', label: '지급완료' },
  { value: 'N', label: '지급대기' },
];

/** 별점을 "★★★☆☆" 형태의 문자열로 표시한다 */
function ratingStars(rating) {
  const n = Math.max(0, Math.min(5, Number(rating) || 0));
  return '★'.repeat(n) + '☆'.repeat(5 - n);
}

/**
 * 새 리뷰 알림용 짧은 "삐-" 비프음을 재생한다
 * 출력까지 알아서 해줘요 - 소리, 화면 알림" 참고). 별도 오디오 파일 없이 Web Audio API의
 * 오실레이터로 생성해 재생하므로 정적 리소스 추가가 필요 없다. 브라우저 자동재생 정책상
 * 사용자가 페이지에서 최소 한 번 이상 클릭/키입력 등의 상호작용을 한 뒤에만 소리가 나므로
 * (관리자가 리뷰관리 화면을 조작 중일 때 알림이 뜨는 상황과 맞아떨어진다), 재생이 막히는
 * 경우도 조용히 무시한다(화면 알림/토스트는 항상 그대로 뜬다).
 */
function playNotificationBeep() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = 880; // A5 - 알림음으로 흔히 쓰이는 높이
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.35);
    oscillator.onended = () => ctx.close();
  } catch {
    // 자동재생 차단 등으로 소리 재생이 실패해도 화면 알림(토스트)은 그대로 노출되므로 무시한다.
  }
}

export default function ReviewManagePage() {
  const { auth } = useAuth();
  const [tab, setTab] = useState('product'); // 'product' | 'store'
  const [refreshSignal, setRefreshSignal] = useState(0);
  const { toasts, pushToast, removeToast } = useToasts();

  // 새 리뷰가 등록되는 즉시 토스트로 알리고, 현재 보고 있는
  // 탭(상품/매장) 목록을 조용히 새로고침한다. 두 탭 모두 어느 화면을 보고 있어도 놓치지 않도록
  // 구독 자체는 탭 전환과 무관하게 이 부모 컴포넌트에서 한 번만 연결한다.
  useEffect(() => {
    if (!auth?.accessToken) return undefined;
    const eventSource = new EventSource(reviewStreamUrl(auth.accessToken));
    eventSource.addEventListener('review-created', (e) => {
      try {
        const data = JSON.parse(e.data);
        const typeLabel = data.reviewType === 'STORE' ? '매장' : '상품';
        const stars = '★'.repeat(Math.max(0, Math.min(5, Number(data.rating) || 0)));
        pushToast(`새 ${typeLabel} 리뷰가 등록되었습니다. ${stars}`);
      } catch {
        pushToast('새 리뷰가 등록되었습니다.');
      }
      playNotificationBeep();
      setRefreshSignal((s) => s + 1);
    });
    // 연결 오류 시 브라우저가 자동으로 재연결을 시도한다.
    eventSource.onerror = () => {};
    return () => eventSource.close();
  }, [auth?.accessToken, pushToast]);

  return (
    <AdminLayout title="리뷰관리" menuUrl="/admin/review">
      <div className="page-header">
        <div>
          <h1>리뷰관리</h1>
          <div className="subtitle">회원이 QR오더 결제완료 건에 대해 작성한 상품/매장 리뷰를 조회하고, 노출 여부를 관리합니다.</div>
        </div>
      </div>

      <div className="field-row" style={{ marginBottom: 16 }}>
        <button
          className={tab === 'product' ? 'btn btn-primary' : 'btn btn-secondary'}
          onClick={() => setTab('product')}
        >
          상품 리뷰
        </button>
        <button
          className={tab === 'store' ? 'btn btn-primary' : 'btn btn-secondary'}
          onClick={() => setTab('store')}
        >
          매장 리뷰
        </button>
      </div>

      {tab === 'product'
        ? <ProductReviewPanel adminName={auth.adminName} refreshSignal={refreshSignal} />
        : <StoreReviewPanel adminName={auth.adminName} refreshSignal={refreshSignal} />}

      <ToastStack toasts={toasts} onDismiss={removeToast} />
    </AdminLayout>
  );
}

function ProductReviewPanel({ adminName, refreshSignal }) {
  const [condition, setCondition] = useState({ pageNo: 1, pageSize: 10, useYn: '' });
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchList = useCallback(async (cond, silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const res = await getAdminProductReviews(cond);
      setPage(res.data);
    } catch (err) {
      if (!silent) setError(err.response?.data?.message || '상품 리뷰 목록을 불러오지 못했습니다.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(condition); }, [condition, fetchList]);

  // 새 상품 리뷰가 등록됐다는 SSE 신호(부모 ReviewManagePage)를 받으면 조용히 다시 조회한다.
  const conditionRef = useRef(condition);
  useEffect(() => { conditionRef.current = condition; }, [condition]);
  useEffect(() => {
    if (refreshSignal) fetchList(conditionRef.current, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  const toggleVisibility = async (review) => {
    try {
      await updateProductReviewVisibility(review.productReviewId, review.useYn === 'Y' ? 'N' : 'Y', adminName);
      fetchList(condition);
    } catch (err) {
      window.alert(err.response?.data?.message || '노출 상태 변경에 실패했습니다.');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteProductReview(deleteTarget.productReviewId);
      setDeleteTarget(null);
      fetchList(condition);
    } catch (err) {
      window.alert(err.response?.data?.message || '리뷰 삭제에 실패했습니다.');
    }
  };

  return (
    <>
      <div className="list-toolbar">
        <SearchBar
          placeholder="검색 조건 없음 (노출여부로만 필터링)"
          extraFilter={{ options: USE_YN_OPTIONS, value: condition.useYn, onChange: (v) => setCondition((c) => ({ ...c, useYn: v, pageNo: 1 })) }}
          onSearch={() => {}}
        />
      </div>

      <div className="list-count-bar">
        <span className="list-count">전체 <b>{page?.totalCount ?? 0}</b>건</span>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>상품</th><th>매장</th><th>작성자</th><th>평점</th><th>내용</th><th>노출</th><th>작성일</th><th>관리</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="empty-row">불러오는 중...</td></tr>}
              {!loading && error && <tr><td colSpan={8} className="empty-row error-state">{error}</td></tr>}
              {!loading && !error && page?.list?.length === 0 && <tr><td colSpan={8} className="empty-row">등록된 상품 리뷰가 없습니다.</td></tr>}
              {!loading && !error && page?.list?.map((r) => (
                <tr key={r.productReviewId}>
                  <td>{r.productName}</td>
                  <td>{r.storeName}</td>
                  <td>{r.memberName}</td>
                  <td>{ratingStars(r.rating)}</td>
                  <td className="text-secondary" style={{ maxWidth: 260, whiteSpace: 'pre-wrap' }}>{r.content || '-'}</td>
                  <td><StatusBadge value={r.useYn} label={r.useYn === 'Y' ? '노출' : '숨김'} /></td>
                  <td>{formatDateTime(r.createdAt)}</td>
                  <td className="actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => toggleVisibility(r)}>
                      {r.useYn === 'Y' ? '숨기기' : '노출하기'}
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(r)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {page && <Pagination {...page} onChange={(pageNo) => setCondition((c) => ({ ...c, pageNo }))} />}

      {deleteTarget && (
        <ConfirmDialog title="상품 리뷰 삭제" message={`'${deleteTarget.memberName}'님이 작성한 리뷰를 삭제하시겠습니까?`}
          confirmLabel="삭제" danger onCancel={() => setDeleteTarget(null)} onConfirm={handleDelete} />
      )}
    </>
  );
}

function StoreReviewPanel({ adminName, refreshSignal }) {
  const [condition, setCondition] = useState({ pageNo: 1, pageSize: 10, useYn: '', rewardGivenYn: '' });
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchList = useCallback(async (cond, silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const res = await getAdminStoreReviews(cond);
      setPage(res.data);
    } catch (err) {
      if (!silent) setError(err.response?.data?.message || '매장 리뷰 목록을 불러오지 못했습니다.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(condition); }, [condition, fetchList]);

  // 새 매장 리뷰가 등록됐다는 SSE 신호(부모 ReviewManagePage)를 받으면 조용히 다시 조회한다.
  const conditionRef = useRef(condition);
  useEffect(() => { conditionRef.current = condition; }, [condition]);
  useEffect(() => {
    if (refreshSignal) fetchList(conditionRef.current, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  const toggleVisibility = async (review) => {
    try {
      await updateStoreReviewVisibility(review.storeReviewId, review.useYn === 'Y' ? 'N' : 'Y', adminName);
      fetchList(condition);
    } catch (err) {
      window.alert(err.response?.data?.message || '노출 상태 변경에 실패했습니다.');
    }
  };

  // 리뷰 유도 리워드(예: 콜라 1캔) 실제 지급 처리 - 포인트 자동 적립이 아니라, 매장 직원이
  // 이미 오프라인으로 건넨 리워드를 관리자가 "지급완료"로 체크해 중복 지급을 막는 수동 기록.
  // 안내(incentiveUseYn) 자체를 꺼둔 매장에는 지급완료 처리를 막는다(서버에서도 동일하게 재검증).
  const toggleReward = async (review) => {
    const nextYn = review.rewardGivenYn === 'Y' ? 'N' : 'Y';
    if (nextYn === 'Y' && review.incentiveUseYn !== 'Y') {
      window.alert('리뷰 유도 리워드 안내를 사용하지 않는 매장입니다. 매장관리에서 안내를 먼저 켜주세요.');
      return;
    }
    if (nextYn === 'N' && !window.confirm('지급완료 표시를 취소하시겠습니까? (지급일시/처리자 기록이 초기화됩니다)')) {
      return;
    }
    try {
      await updateStoreReviewReward(review.storeReviewId, nextYn, adminName);
      fetchList(condition);
    } catch (err) {
      window.alert(err.response?.data?.message || '리워드 지급 상태 변경에 실패했습니다.');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteStoreReview(deleteTarget.storeReviewId);
      setDeleteTarget(null);
      fetchList(condition);
    } catch (err) {
      window.alert(err.response?.data?.message || '리뷰 삭제에 실패했습니다.');
    }
  };

  return (
    <>
      <div className="list-toolbar">
        <div className="field-row" style={{ gap: 8, flex: 1 }}>
          <SearchBar
            placeholder="검색 조건 없음 (노출여부/리워드 지급여부로만 필터링)"
            extraFilter={{ options: USE_YN_OPTIONS, value: condition.useYn, onChange: (v) => setCondition((c) => ({ ...c, useYn: v, pageNo: 1 })) }}
            onSearch={() => {}}
          />
          <select
            className="select"
            value={condition.rewardGivenYn}
            onChange={(e) => setCondition((c) => ({ ...c, rewardGivenYn: e.target.value, pageNo: 1 }))}
            title="리워드 지급여부"
          >
            {REWARD_YN_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label === '전체' ? '리워드 전체' : opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="list-count-bar">
        <span className="list-count">전체 <b>{page?.totalCount ?? 0}</b>건</span>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>매장</th><th>작성자</th><th>평점</th><th>내용</th><th>노출</th><th>리워드</th><th>작성일</th><th>관리</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="empty-row">불러오는 중...</td></tr>}
              {!loading && error && <tr><td colSpan={8} className="empty-row error-state">{error}</td></tr>}
              {!loading && !error && page?.list?.length === 0 && <tr><td colSpan={8} className="empty-row">등록된 매장 리뷰가 없습니다.</td></tr>}
              {!loading && !error && page?.list?.map((r) => (
                <tr key={r.storeReviewId}>
                  <td>{r.storeName}</td>
                  <td>{r.memberName}</td>
                  <td>{ratingStars(r.rating)}</td>
                  <td className="text-secondary" style={{ maxWidth: 260, whiteSpace: 'pre-wrap' }}>{r.content || '-'}</td>
                  <td><StatusBadge value={r.useYn} label={r.useYn === 'Y' ? '노출' : '숨김'} /></td>
                  <td>
                    {r.rewardGivenYn === 'Y'
                      ? <StatusBadge value="Y" label="지급완료" />
                      : r.incentiveUseYn === 'Y'
                        ? <StatusBadge value="N" label="지급대기" />
                        : <StatusBadge value="N" label="리워드 미사용 매장" />}
                    {r.rewardGivenYn === 'Y' && r.rewardGivenAt && (
                      <div className="text-secondary" style={{ fontSize: 12, marginTop: 2 }}>{formatDateTime(r.rewardGivenAt)}</div>
                    )}
                  </td>
                  <td>{formatDateTime(r.createdAt)}</td>
                  <td className="actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => toggleVisibility(r)}>
                      {r.useYn === 'Y' ? '숨기기' : '노출하기'}
                    </button>
                    {(r.rewardGivenYn === 'Y' || r.incentiveUseYn === 'Y') && (
                      <button className="btn btn-secondary btn-sm" onClick={() => toggleReward(r)}>
                        {r.rewardGivenYn === 'Y' ? '지급취소' : '리워드 지급완료'}
                      </button>
                    )}
                    <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(r)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {page && <Pagination {...page} onChange={(pageNo) => setCondition((c) => ({ ...c, pageNo }))} />}

      {deleteTarget && (
        <ConfirmDialog title="매장 리뷰 삭제" message={`'${deleteTarget.memberName}'님이 작성한 리뷰를 삭제하시겠습니까?`}
          confirmLabel="삭제" danger onCancel={() => setDeleteTarget(null)} onConfirm={handleDelete} />
      )}
    </>
  );
}
